import Dexie, { type Table } from 'dexie'
import type { Arrangement, Practice, Revision, Song } from '../domain'

/**
 * Base de datos local (IndexedDB con Dexie). Cada canción, arreglo y revisión es un registro
 * propio: las revisiones son inmutables, así que una edición solo añade un registro y mueve el
 * puntero `mainRevisionId` del arreglo.
 *
 * Migraciones: cada versión nueva se declara con `db.version(n).stores({...})` (y `.upgrade(...)`
 * si hay que transformar datos) y lleva una prueba que parte de datos escritos con el esquema
 * anterior (docs/decisiones/0005). La versión 2 (E2) añade la memoria de práctica: es una tabla
 * nueva y no transforma nada.
 */
export interface MetaRecord {
  key: string
  value: number | string | null
}

/** Lo que había en el editor sin guardar: sobrevive a que iPadOS cierre la app (§9.5). */
export interface EditorDraft {
  arrangementId: string
  /** La revisión principal de la que partía el borrador. */
  baseRevisionId: string
  title: string
  artist: string
  body: string
  shapeKey: string
  capo: number
  tempoBpm: number | null
  timeSignature: string
  notes: string
  /** Se había pulsado «La he revisado». */
  reviewed?: boolean
  savedAt: string
}

export type AtrilDb = Dexie & {
  songs: Table<Song, string>
  arrangements: Table<Arrangement, string>
  revisions: Table<Revision, string>
  drafts: Table<EditorDraft, string>
  meta: Table<MetaRecord, string>
  practice: Table<Practice, string>
}

export const DB_NAME = 'atril'

/** El esquema de la versión 1 (E1). Se conserva tal cual: las migraciones parten de él. */
export const SCHEMA_V1 = {
  songs: 'id, createdAt',
  arrangements: 'id, songId',
  revisions: 'id, arrangementId, createdAt',
  drafts: 'arrangementId',
  meta: 'key',
} as const

export function createDb(name: string = DB_NAME): AtrilDb {
  const db = new Dexie(name) as AtrilDb
  db.version(1).stores(SCHEMA_V1)
  db.version(2).stores({ practice: 'arrangementId' })
  return db
}

let shared: AtrilDb | null = null

/** La base de datos de la app: una sola conexión por pestaña. */
export function getDb(): AtrilDb {
  shared ??= createDb()
  return shared
}
